<template>
    <v-form ref="formRef" class="d-flex flex-column flex-grow-1 overflow-hidden min-h-0" @submit.prevent="onSubmit">
        <div class="reserve-form-fields flex-grow-1 overflow-y-auto overflow-x-hidden min-h-0 pt-2">
        <v-row>
            <template v-if="entryType === 'ware'">
                <v-col cols="12">
                    <ReservationDriverFields
                        :model-value="driverFields"
                        required
                        @update:model-value="onDriverFieldsUpdate"
                    />
                </v-col>
            </template>
            <v-col cols="12">
                <v-list-subheader class="px-0">{{ $t('reservation.loadingSection') }}</v-list-subheader>
            </v-col>
            <v-col
                v-for="(row, index) in loadingSchedules"
                :key="`loading-${row.locationId}`"
                cols="12"
                class="reserve-stop-row"
            >
                <v-row dense>
                    <v-col cols="12" md="4">
                        <v-text-field
                            :model-value="row.placeLabel"
                            :label="$t('reservation.loadingSection')"
                            variant="outlined"
                            hide-details="auto"
                            readonly
                            disabled
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-tooltip
                            :text="row.constraints.dateDisabledTooltip"
                            location="top"
                            :disabled="!row.constraints.dateDisabled"
                        >
                            <template #activator="{ props }">
                                <div v-bind="props">
                                    <FormDate
                                        ref="formLoadingDateRefs"
                                        :model-value="row.schedule"
                                        :data-cy-prefix="`reservation-loading-${index}`"
                                        :min="row.constraints.dateMin"
                                        :max="row.constraints.dateMax"
                                        :disabled="row.constraints.dateDisabled"
                                        single
                                        @update:model-value="row.schedule = $event"
                                    />
                                </div>
                            </template>
                        </v-tooltip>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-tooltip
                            :text="row.constraints.timeDisabledTooltip"
                            location="top"
                            :disabled="!row.constraints.timeDisabled"
                        >
                            <template #activator="{ props }">
                                <div v-bind="props">
                                    <FormTime
                                        :model-value="row.schedule"
                                        :data-cy-prefix="`reservation-loading-${index}`"
                                        :min="row.constraints.timeMin"
                                        :max="row.constraints.timeMax"
                                        :disabled="row.constraints.timeDisabled"
                                        required
                                        @update:model-value="row.schedule = $event"
                                    />
                                </div>
                            </template>
                        </v-tooltip>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12">
                <v-list-subheader class="px-0">{{ $t('reservation.unloadingSection') }}</v-list-subheader>
            </v-col>
            <v-col
                v-for="(row, index) in unloadingSchedules"
                :key="`unloading-${row.locationId}`"
                cols="12"
                class="reserve-stop-row"
            >
                <v-row dense>
                    <v-col cols="12" md="4">
                        <v-text-field
                            :model-value="row.placeLabel"
                            :label="$t('reservation.unloadingSection')"
                            variant="outlined"
                            hide-details="auto"
                            readonly
                            disabled
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-tooltip
                            :text="row.constraints.dateDisabledTooltip"
                            location="top"
                            :disabled="!row.constraints.dateDisabled"
                        >
                            <template #activator="{ props }">
                                <div v-bind="props">
                                    <FormDate
                                        ref="formUnloadingDateRefs"
                                        :model-value="row.schedule"
                                        :data-cy-prefix="`reservation-unloading-${index}`"
                                        :min="row.constraints.dateMin"
                                        :max="row.constraints.dateMax"
                                        :disabled="row.constraints.dateDisabled"
                                        single
                                        @update:model-value="row.schedule = $event"
                                    />
                                </div>
                            </template>
                        </v-tooltip>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-tooltip
                            :text="row.constraints.timeDisabledTooltip"
                            location="top"
                            :disabled="!row.constraints.timeDisabled"
                        >
                            <template #activator="{ props }">
                                <div v-bind="props">
                                    <FormTime
                                        :model-value="row.schedule"
                                        :data-cy-prefix="`reservation-unloading-${index}`"
                                        :min="row.constraints.timeMin"
                                        :max="row.constraints.timeMax"
                                        :disabled="row.constraints.timeDisabled"
                                        required
                                        @update:model-value="row.schedule = $event"
                                    />
                                </div>
                            </template>
                        </v-tooltip>
                    </v-col>
                </v-row>
            </v-col>
            <template v-if="entryType === 'truck'">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="formData.cargo_weight"
                        :label="$t('reservation.cargoWeight')"
                        type="number"
                        step="0.01"
                        variant="outlined"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="formData.cargo_mdp"
                        :label="$t('reservation.cargoMdp')"
                        type="number"
                        step="0.01"
                        variant="outlined"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12">
                    <v-textarea
                        v-model="formData.special_transport_notes"
                        :label="$t('reservation.specialTransportNotes')"
                        variant="outlined"
                        rows="2"
                        hide-details="auto"
                    />
                </v-col>
            </template>
            <template v-if="showsOpeningOfferForUnlistedEntry">
                <v-col cols="12">
                    <v-list-subheader class="px-0">{{ $t("reservation.pricingSection") }}</v-list-subheader>
                </v-col>
                <v-col cols="12" lg="4" md="4" sm="12">
                    <v-tooltip :text="$t('reservation.openingOfferOnlyTooltip')" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <div v-bind="tooltipProps">
                                <v-text-field
                                    :model-value="priceOfferValue"
                                    :label="$t('reservation.openingOfferOnly')"
                                    variant="outlined"
                                    hide-details="auto"
                                    type="number"
                                    :rules="openingOfferAmountRules"
                                    @update:modelValue="onPriceOfferModelValueUpdate"
                                />
                            </div>
                        </template>
                    </v-tooltip>
                </v-col>
                <v-col v-if="showsOpeningOfferForUnlistedEntry" cols="12" lg="8" md="8" sm="12">
                    <PriceTermsFields
                        ref="openingOfferPriceTermsRef"
                        v-model="openingOfferTerms"
                    />
                </v-col>
                <v-expand-transition>
                    <v-col v-if="priceOfferWarningVisible" cols="12">
                        <p class="text-error text-body-2 mb-0">{{ $t("reservation.priceOfferWarning") }}</p>
                    </v-col>
                </v-expand-transition>
            </template>
            <template v-else-if="showsListedPriceWithOptionalProposedOffer">
                <v-col cols="12">
                    <v-list-subheader class="px-0">{{ $t("reservation.pricingSection") }}</v-list-subheader>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-tooltip :text="$t('reservation.currentPriceTooltip')" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <div v-bind="tooltipProps">
                                <v-text-field
                                    :model-value="currentPriceDisplay"
                                    :label="$t('reservation.currentPrice')"
                                    variant="outlined"
                                    hide-details="auto"
                                    readonly
                                    disabled
                                />
                            </div>
                        </template>
                    </v-tooltip>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-tooltip :text="$t('reservation.proposedPriceTooltip')" location="top">
                        <template #activator="{ props: tooltipProps }">
                            <div v-bind="tooltipProps">
                                <v-text-field
                                    :model-value="priceOfferValue"
                                    :label="$t('reservation.proposedPrice')"
                                    variant="outlined"
                                    hide-details="auto"
                                    type="number"
                                    @update:modelValue="onPriceOfferModelValueUpdate"
                                />
                            </div>
                        </template>
                    </v-tooltip>
                </v-col>
                <v-expand-transition>
                    <v-col v-if="priceOfferWarningVisible" cols="12">
                        <p class="text-error text-body-2 mb-0">{{ $t("reservation.priceOfferWarning") }}</p>
                    </v-col>
                </v-expand-transition>
            </template>
        </v-row>
        </div>
        <v-card-actions class="reserve-form-actions flex-shrink-0 px-0 pb-0 pt-4">
            <v-row dense class="w-100 ma-0">
                <v-col cols="6" class="pa-1">
                    <v-btn block variant="flat" color="error" @click="$emit('cancel')">
                        {{ $t('reservation.cancel') }}
                    </v-btn>
                </v-col>
                <v-col cols="6" class="pa-1">
                    <v-btn block variant="flat" color="primary" type="submit" :loading="submitting">
                        {{ $t(submitButtonLabelKey) }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-actions>
    </v-form>
</template>

<script>
import FormDate from "@/components/forms/FormDate.vue";
import FormTime from "@/components/forms/FormTime.vue";
import PriceTermsFields from "@/components/forms/PriceTermsFields.vue";
import ReservationDriverFields from "@/components/reservation/ReservationDriverFields.vue";
import { cleanPriceInput, formatToYmd, formatToHhMm } from "@/utils";

export default {
    components: { FormDate, FormTime, PriceTermsFields, ReservationDriverFields },
    props: {
        entryType: {
            type: String,
            required: true,
            validator: (value) => ["truck", "ware"].includes(value),
        },
        entryInfo: {
            type: Object,
            default: null,
        },
        loadingLocation: {
            type: Object,
            default: null,
        },
        unloadingLocation: {
            type: Object,
            default: null,
        },
        loadingLocations: {
            type: Array,
            default: () => [],
        },
        unloadingLocations: {
            type: Array,
            default: () => [],
        },
        hideActions: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["submit", "cancel"],
    data() {
        return {
            submitting: false,
            formData: {
                car_number: "",
                driver_name: "",
                driver_surname: "",
                customs: "",
                cargo_weight: null,
                cargo_mdp: null,
                special_transport_notes: "",
            },
            loadingSchedules: [],
            unloadingSchedules: [],
            requiredRule: (value) => !!value || this.$t("validation.required"),
            priceOfferValue: null,
            openingOfferTerms: { currency: null, vatType: false },
        };
    },
    computed: {
        entryHasListedPrice() {
            const listPrice = this.entryInfo?.price;
            return listPrice != null && listPrice !== "";
        },
        showsListedPriceWithOptionalProposedOffer() {
            return Boolean(this.entryInfo) && this.entryHasListedPrice;
        },
        showsOpeningOfferForUnlistedEntry() {
            return Boolean(this.entryInfo) && !this.entryHasListedPrice;
        },
        submitButtonLabelKey() {
            if (this.showsOpeningOfferForUnlistedEntry) {
                return "reservation.submitWithOffer";
            }
            const reservationIncludesPriceOffer = this.priceOfferValue != null;
            return reservationIncludesPriceOffer ? "reservation.submitWithOffer" : "reservation.submit";
        },
        currentPriceDisplay() {
            if (!this.entryInfo) return "";
            const amount = this.entryInfo.price;
            if (amount == null || amount === "") return "";
            const cur = this.entryInfo.currency || "";
            const vatLabel =
                this.entryInfo.vat_type === true
                    ? this.$t("formFields.withVAT")
                    : this.$t("formFields.withoutVAT");
            return `${amount} ${cur} ${vatLabel}`.trim();
        },
        priceOfferWarningVisible() {
            return this.priceOfferValue != null;
        },
        /** Required amount rules for listings without a fixed list price (opening offer only). */
        openingOfferAmountRules() {
            if (!this.showsOpeningOfferForUnlistedEntry) return [];
            return [
                (inputValue) => {
                    const normalizedPrice = cleanPriceInput(inputValue);
                    if (normalizedPrice == null) return this.$t("validation.required");
                    if (normalizedPrice <= 0) return this.$t("validation.required");
                    return true;
                },
            ];
        },
        resolvedLoadingLocations() {
            if (this.loadingLocations.length) return this.loadingLocations;
            return this.loadingLocation ? [this.loadingLocation] : [];
        },
        resolvedUnloadingLocations() {
            if (this.unloadingLocations.length) return this.unloadingLocations;
            return this.unloadingLocation ? [this.unloadingLocation] : [];
        },
        driverFields() {
            return {
                car_number: this.formData.car_number,
                driver_name: this.formData.driver_name,
                driver_surname: this.formData.driver_surname,
                customs: this.formData.customs,
            };
        },
    },
    watch: {
        resolvedLoadingLocations: {
            handler(locations) {
                this.loadingSchedules = this.schedulesForLocations(locations, this.loadingSchedules);
            },
            immediate: true,
        },
        resolvedUnloadingLocations: {
            handler(locations) {
                this.unloadingSchedules = this.schedulesForLocations(locations, this.unloadingSchedules);
            },
            immediate: true,
        },
    },
    methods: {
        schedulesForLocations(locations, currentSchedules) {
            const next = locations || [];
            const current = currentSchedules || [];
            const sameStops =
                next.length > 0 &&
                next.length === current.length &&
                next.every((location, index) => location?.id === current[index]?.locationId);
            if (sameStops) return current;
            return next.map((location) => this.buildScheduleRow(location));
        },
        placeLabel(location) {
            const country = location?.countryName?.name || location?.country_name || "";
            const place = location?.place_display_name || location?.cityName || location?.city || "";
            const city = String(place).split(",")[0].trim();
            return [country, city].filter(Boolean).join(", ");
        },
        buildScheduleRow(location) {
            const constraints = this._constraintsFromLocation(location);
            const schedule = {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: "",
                timeEnd: "",
            };
            if (constraints.dateMin) {
                schedule.date = [new Date(`${constraints.dateMin}T12:00:00`)];
                schedule.dateStart = constraints.dateMin;
                schedule.dateEnd = constraints.dateMax;
            }
            if (constraints.timeDisabled && constraints.timeMin) {
                schedule.timeStart = constraints.timeMin;
                schedule.timeEnd = constraints.timeMax;
            }
            return {
                locationId: location?.id,
                placeLabel: this.placeLabel(location),
                constraints,
                schedule,
            };
        },
        async validateDateRefs(refName) {
            const refs = this.$refs[refName];
            const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
            if (!list.length) return false;
            let isValid = true;
            for (const component of list) {
                const valid = await component?.validateFields?.();
                if (!valid) isValid = false;
            }
            return isValid;
        },
        _constraintsFromLocation(location) {
            const result = {
                dateMin: null,
                dateMax: null,
                dateDisabled: false,
                dateDisabledTooltip: "",
                timeMin: null,
                timeMax: null,
                timeDisabled: false,
                timeDisabledTooltip: "",
            };
            if (!location || !location.date_start || !location.date_end) return result;

            const dateStart = formatToYmd(location.date_start);
            const dateEnd = formatToYmd(location.date_end);
            if (!dateStart || !dateEnd) return result;

            result.dateMin = dateStart;
            result.dateMax = dateEnd;
            if (dateStart === dateEnd) {
                result.dateDisabled = true;
                result.dateDisabledTooltip = this.$t("reservation.singleDateTooltip");
            }

            const timeStart = formatToHhMm(location.time_start);
            const timeEnd = formatToHhMm(location.time_end);
            if (timeStart || timeEnd) {
                if (timeStart && timeEnd && timeStart === timeEnd) {
                    result.timeMin = timeStart;
                    result.timeMax = timeEnd;
                    result.timeDisabled = true;
                    result.timeDisabledTooltip = this.$t("reservation.singleTimeTooltip");
                } else {
                    result.timeMin = timeStart || "00:00";
                    result.timeMax = timeEnd || "23:59";
                }
            }
            return result;
        },
        onDriverFieldsUpdate(nextDriverFields) {
            this.formData.car_number = nextDriverFields.car_number;
            this.formData.driver_name = nextDriverFields.driver_name;
            this.formData.driver_surname = nextDriverFields.driver_surname;
            this.formData.customs = nextDriverFields.customs;
        },
        async onSubmit() {
            const loadingDateValid = await this.validateDateRefs("formLoadingDateRefs");
            const unloadingDateValid = await this.validateDateRefs("formUnloadingDateRefs");
            const formValid = await this.$refs.formRef.validate();
            if (!formValid?.valid || !loadingDateValid || !unloadingDateValid) return;

            if (this.showsOpeningOfferForUnlistedEntry) {
                const normalizedOpeningOffer = cleanPriceInput(this.priceOfferValue);
                if (normalizedOpeningOffer == null || normalizedOpeningOffer <= 0) return;
                const openingTermsValid = await this.$refs.openingOfferPriceTermsRef?.validateFields?.();
                if (!openingTermsValid) return;
            }

            const timeStr = (t) => (typeof t === "string" ? t : "00:00").substring(0, 5);
            const schedulePayload = (row, role) => ({
                location_id: row.locationId,
                role,
                date: row.schedule.dateStart,
                time_start: timeStr(row.schedule.timeStart),
                time_end: timeStr(row.schedule.timeEnd),
            });
            const loadingRows = this.loadingSchedules;
            const unloadingRows = this.unloadingSchedules;
            const load = loadingRows[0]?.schedule;
            const unload = unloadingRows[unloadingRows.length - 1]?.schedule;
            if (!load?.dateStart || !load?.timeStart || !load?.timeEnd) return;
            if (!unload?.dateStart || !unload?.timeStart || !unload?.timeEnd) return;
            if (loadingRows.some((row) => !row.schedule.dateStart || !row.schedule.timeStart || !row.schedule.timeEnd)) return;
            if (unloadingRows.some((row) => !row.schedule.dateStart || !row.schedule.timeStart || !row.schedule.timeEnd)) return;

            const payload = {
                car_number: this.entryType === "ware" ? this.formData.car_number : "",
                driver_name: this.entryType === "ware" ? this.formData.driver_name : "",
                driver_surname: this.entryType === "ware" ? this.formData.driver_surname : "",
                customs: this.entryType === "ware" ? (this.formData.customs || "").trim() : "",
                stop_schedules: [
                    ...loadingRows.map((row) => schedulePayload(row, "loading")),
                    ...unloadingRows.map((row) => schedulePayload(row, "unloading")),
                ],
                chosen_loading_date: load.dateStart,
                chosen_loading_time_start: timeStr(load.timeStart),
                chosen_loading_time_end: timeStr(load.timeEnd),
                chosen_unloading_date: unload.dateStart,
                chosen_unloading_time_start: timeStr(unload.timeStart),
                chosen_unloading_time_end: timeStr(unload.timeEnd),
            };

            if (this.entryType === "truck") {
                payload.cargo_weight = this.formData.cargo_weight ?? null;
                payload.cargo_mdp = this.formData.cargo_mdp ?? null;
                payload.special_transport_notes =
                    this.formData.special_transport_notes || null;
            }

            if (this.priceOfferValue != null) {
                payload.price_offer = this.priceOfferValue;
                if (this.showsOpeningOfferForUnlistedEntry) {
                    payload.price_offer_currency = this.openingOfferTerms.currency;
                    payload.price_offer_vat_type = this.openingOfferTerms.vatType;
                }
            }

            this.$emit("submit", payload);
        },
        onPriceOfferModelValueUpdate(nextValue) {
            this.priceOfferValue = cleanPriceInput(nextValue);
        },
        setSubmitting(value) {
            this.submitting = value;
        },
    },
};
</script>
